import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const categories = [
  {
    title: "Specialty, Artisan & Subscription Markets",
    image: "/Images/markets/Specialty, Artisan & Subscription Markets.webp",
    description: "Curated unboxing experiences for niche products."
  },
  {
    title: "Food & Beverages",
    image: "/Images/markets/Food & Beverages.webp",
    description: "Safe, sustainable, and appetizing cartons."
  },
  {
    title: "Electronics",
    image: "/Images/markets/Electronics.webp",
    description: "High-protection packaging for tech innovation."
  },
  {
    title: "Cosmetics & Beauty",
    image: "/Images/markets/Cosmetics & Beauty.webp",
    description: "Luxurious visuals for premium aesthetic appeal."
  },
  {
    title: "Retail, Fashion & Lifestyle",
    image: "/Images/markets/Retail, Fashion & Lifestyle.webp",
    description: "Trendy designs that speak to modern style."
  },
  {
    title: "Pharmaceuticals & Healthcare",
    image: "/Images/markets/Pharmaceuticals & Healthcare.webp",
    description: "Compliant, secure, and clear communication."
  }
];

export const MarketGrid = () => {
  return (
    <section className="py-20 bg-background">
      <div className="w-[90vw] max-w-none mx-auto">
        {/* 2x3 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {categories.map((cat, index) => (
            <Card key={cat.title} item={cat} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Card = ({ item, index }: { item: typeof categories[0], index: number }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
      className="relative h-[350px] md:h-[500px] rounded-3xl overflow-hidden cursor-pointer group shadow-xl"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Image with Blur Effect */}
      <motion.div
        className="absolute inset-0 w-full h-full"
        animate={{ 
          scale: isHovered ? 1.1 : 1,
          filter: isHovered ? "blur(8px) brightness(0.7)" : "blur(0px) brightness(1)"
        }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        <img 
          src={item.image} 
          alt={item.title} 
          className="w-full h-full object-cover"
        />
        {/* Optional: Gradient overlay for better text contrast if needed, 
            but brightness filter handles some of it. 
            Adding a subtle gradient at bottom helps initial visibility if we wanted text always visible. 
        */}
      </motion.div>

      {/* Hover Content - Centered */}
      <AnimatePresence>
        {isHovered && (
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "circOut" }}
            className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center z-10"
          >
            <h3 className="font-heading font-bold text-3xl text-white mb-4 drop-shadow-md">
              {item.title}
            </h3>
            <div className="w-12 h-1 bg-[#FF4D6D] rounded-full mb-4" />
            <p className="text-white/90 text-lg font-medium leading-relaxed max-w-xs drop-shadow-sm">
              {item.description}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Default State - Maybe show just the Title at bottom? 
          User said "on hover will show text about the product".
          Let's keep it clean: Clean image, then hover reveals all.
      */}
    </motion.div>
  );
};
